import { JSX, useCallback, useState } from 'react';
import { CreateButton, Title, usePermissions } from 'react-admin';
import {
    Box,
    InputAdornment,
    TextField,
    Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { permissionManager } from "../permissions/manager";
import { settingsSections } from "../../defaults/defaults";
import { SettingsSectionCard, SettingsSection } from "./components/settings/SettingsSectionCard";

/**
 * The settings index.
 *
 * This was six react-admin <List> components laid out in a two-column grid,
 * each one locked to a 600px cell: six Create buttons, six paginations, six
 * sortable table headers for a single column, and a lot of empty space under
 * the short sections. Each row showed a label and nothing else, so the only way
 * to find out what a setting was set to was to open it.
 *
 * It is now one page: a single header, one search across every section, and
 * cards that size to their contents and show each setting's current value.
 */
export const SettingsList: () => JSX.Element = (): JSX.Element => {
    const { permissions } = usePermissions();
    const canEdit: boolean = permissionManager.isAllowed(permissions, 'HQ_Admin::settings.edit');

    const [query, setQuery] = useState<string>('');
    const [counts, setCounts] = useState<Record<string, number>>({});

    // Sections report their own match count so the page can tell "still
    // loading" from "genuinely nothing matches". -1 means the section is
    // pending. Stable identity, or the child's effect would loop.
    const handleCount: (path: string, matches: number) => void = useCallback(
        (path: string, matches: number): void => {
            setCounts((previous: Record<string, number>): Record<string, number> =>
                previous[path] === matches ? previous : { ...previous, [path]: matches });
        },
        []
    );

    const reported: number[] = Object.values(counts);
    const pending: boolean =
        reported.length < settingsSections.length || reported.some((n: number): boolean => n === -1);
    const total: number = reported
        .filter((n: number): boolean => n >= 0)
        .reduce((sum: number, n: number): number => sum + n, 0);

    const searching: boolean = query.trim().length > 0;

    return (
        <Box sx={{ pb: 8 }}>
            <Title title="Settings" />

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    gap: 2,
                    pt: 3,
                    pb: 2.5,
                }}
            >
                <Box sx={{ minWidth: 0, mr: 'auto' }}>
                    <Typography component="h1" sx={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}>
                        { 'Settings' }
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 13.5, color: 'text.secondary' }}>
                        { 'Integrations, email delivery and system defaults. Changes apply straight away.' }
                    </Typography>
                </Box>

                <TextField
                    size="small"
                    value={ query }
                    placeholder="Search settings"
                    onChange={ (event): void => setQuery(event.target.value) }
                    sx={{ width: { xs: '100%', sm: 260 } }}
                    slotProps={{
                        // The field has no visible label, so the accessible
                        // name has to go on the input itself — as a TextField
                        // prop it lands on the wrapping FormControl instead.
                        htmlInput: { 'aria-label': 'Search settings' },
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                { canEdit &&
                    <CreateButton variant="contained" label="New setting" />
                }
            </Box>

            { (searching && !pending) &&
                <Typography sx={{ pb: 1.5, fontSize: 12.5, color: 'text.secondary' }}>
                    { total === 0
                        ? `Nothing matches “${query.trim()}”.`
                        : `${total} setting${total === 1 ? '' : 's'} match “${query.trim()}”.` }
                </Typography>
            }

            { /* Columns rather than a grid: sections vary from three rows to
                 thirty, and a grid row would stretch every card to match its
                 tallest neighbour — which is where the old dead space came from. */ }
            <Box sx={{ columnCount: { xs: 1, lg: 2, xl: 3 }, columnGap: 2.5 }}>
                { settingsSections.map((section: SettingsSection): JSX.Element => (
                    <SettingsSectionCard
                        key={ section.path }
                        section={ section }
                        query={ query }
                        canEdit={ canEdit }
                        onCount={ handleCount }
                    />
                )) }
            </Box>
        </Box>
    );
}
